import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AISparkles } from "@/components/ui/ai-sparkles";
import type { IntentType, HeroAsinWithIntent, ContentType, ABTestType, AxisFingerprint, ParsedField, GenerateRationaleResponse } from "@/types/ab-tests";
import { emptyStructureTreatments } from "./StructurePanel";
import { useDetectFingerprint, useExtractProductName } from "@/resources/ab-test/ab-test.api";
import type { ZoneSelection } from "./PositioningEditor";
import { ZoneEditPanel } from "./ZoneEditPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SPIScorecard } from "@/components/spi/SPIScorecard";
import { RationaleDrawer } from "./RationaleDrawer";
import type { AiValidationResult } from "@/types/ai-validation";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAppData } from "@/contexts/AppDataContext";
import { productApi } from "@/resources/product";
import { ValidationPanel } from "@/components/ui/ValidationPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useListScorecards, useGetScorecard } from "@/resources/scorecard/scorecard.api";
import type { CosmoQuestion } from "@/types/scorecard";
import { FieldSelectionStep } from "./FieldSelectionStep";
import { CreateTestFullPageHeader } from "./CreateTestFullPageHeader";
import { CreateTestFullPageFooter } from "./CreateTestFullPageFooter";
import { VariantAPane } from "./VariantAPane";
import { VariantBPane } from "./VariantBPane";
import { KeywordBankPicker } from "./KeywordBankPicker";
import { StrategyBrowserStates } from "@/components/seo-strategy/StrategyBrowserStates";
import type { Segment } from "@/components/seo-strategy/StrategyBrowser";
import { writeSessionDraft, clearSessionDraft } from "./session-draft";
import { StepIndicator } from "./StepIndicator";
import { TestSuccessDialog } from "./TestSuccessDialog";
import { ExitConfirmDialog } from "./ExitConfirmDialog";
import { EditFieldsDialog } from "./EditFieldsDialog";
import { RationaleSlot } from "./RationaleSlot";
import { useVariantBActions } from "@/hooks/useVariantBActions";
import { useRevalidateVariantB } from "@/hooks/useRevalidateVariantB";
import { useTestModalInit } from "@/hooks/useTestModalInit";
import { useTestSubmit } from "@/hooks/useTestSubmit";
import { useCreateTestLogic } from "@/hooks/useCreateTestLogic";
import { useUndoRedo } from "@/hooks/useUndoRedo";
import type { ScorecardGapSnapshot, CreatedAbTestPayload, CreateTestModalProps } from "./create-test-modal.types";

export type { ABTestType } from "@/types/ab-tests";
export type { CreatedAbTestPayload, ScorecardPrefill, SignalPrefill, KeywordPrefill, CreateTestModalProps } from "./create-test-modal.types";

const KEYWORDS_PAGE_SIZE = 50;

export function CreateTestModal({
  fullPage = false, open: openProp = true, onOpenChange: onOpenChangeProp,
  heroAsin, onCreateTest, onCancel, initialTestType, initialTargetPhrase,
  prefillFromScorecard, prefillFromSignal, prefillFromKeyword, prefillFromDraft,
  initialStrategyLinkId,
}: CreateTestModalProps) {
  const open = fullPage ? true : openProp;
  const onOpenChange = onOpenChangeProp ?? (() => {});
  const handleCancel = fullPage ? (onCancel ?? (() => {})) : () => onOpenChange(false);
  const { selectedBrandId } = useAppData();
  const parsedProductId = String(heroAsin.id).replace(/^hero-/i, "");

  const {
    data: productKeywords,
    isLoading: isLoadingKeywords,
    isError: isErrorKeywords,
    refetch: refetchKeywords,
  } = productApi.useGetHeroProductKeywords(parsedProductId, selectedBrandId);

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedFields, setSelectedFields] = useState<ContentType[]>(["title"]);
  const [targetPhrase, setTargetPhrase] = useState("");
  // AUT-430 (WEB-008): FR-15/FR-16 -- the strategy this test is linked to. Initialised from the
  // launch prop and re-set by useTestModalInit on every open; its setter is exposed to the apply
  // hooks so a successful `Use this strategy` (WEB-010) can point it at the applied strategy.
  const [strategyLinkId, setStrategyLinkId] = useState<number | null | undefined>(initialStrategyLinkId);
  // The target phrase's value when the page opened (H2) -- written only by useTestModalInit, read
  // back by Reset. A ref: it must not trigger a render or feed the session-draft persist effect.
  const openTargetPhraseRef = useRef("");
  const [visibleKeywordCount, setVisibleKeywordCount] = useState(KEYWORDS_PAGE_SIZE);
  const [intentFrom, setIntentFrom] = useState<IntentType>(heroAsin.declaredIntent.primary);
  const [intentTo, setIntentTo] = useState<IntentType | "">("");
  const [hypothesis, setHypothesis] = useState("");
  const [variantATitle, setVariantATitle] = useState("");
  const [variantABullets, setVariantABullets] = useState<Record<string, string>>({});
  const [variantBTitle, setVariantBTitle] = useState("");
  const [variantBBullets, setVariantBBullets] = useState<Record<string, string>>({});
  const [copyValidation, setCopyValidation] = useState<AiValidationResult | null>(null);
  const [hypothesisValidation, setHypothesisValidation] = useState<AiValidationResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [variantBMode, setVariantBMode] = useState<"use-ai" | "starting-point" | "manual-editor">("use-ai");
  // AUT-430 (WEB-011), FR-1/FR-2: which of the two build tabs is open. Starts on `Strategies` for
  // a strategy or audit prefill (FR-2), `Keywords` otherwise -- including a resumed server draft
  // and a restored session draft, neither of which sets `prefillFromScorecard`. Not persisted.
  const [buildTab, setBuildTab] = useState<"keywords" | "strategies">(prefillFromScorecard ? "strategies" : "keywords");
  // AUT-430 (WEB-011), FR-4: the Strategies tab's segment, lifted here so it survives switching to
  // `Keywords` and back within one creation session (the chip itself stays internal to
  // `StrategyBrowser` and resets, since its `TabsContent` unmounts when not active). Not persisted.
  const [strategySegment, setStrategySegment] = useState<Segment>("prioritized");
  const [structureTreatments, setStructureTreatments] = useState(emptyStructureTreatments());
  const [lastSource, setLastSource] = useState<string | null>(null);
  const [lastSourceDetail, setLastSourceDetail] = useState("");
  // AUT-430 (WEB-003/WEB-010): the Variant B header's AI status alert -- `"applying"` while a
  // keyword or strategy apply is in flight, `"applied"` for the strategy apply's persistent note
  // afterwards. Cleared when the next apply starts (inside useVariantBActions) and by Reset; Undo
  // and Redo leave it (it is not part of VariantBSnapshot).
  const [aiStatus, setAiStatus] = useState<{ state: "applying" | "applied"; text: string } | null>(null);
  const [appliedScorecardGaps, setAppliedScorecardGaps] = useState<string[]>([]);
  const [gapSnapshots, setGapSnapshots] = useState<Record<string, ScorecardGapSnapshot>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successPayload, setSuccessPayload] = useState<CreatedAbTestPayload | null>(null);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [rationaleOpen, setRationaleOpen] = useState(false);
  const [rationaleData, setRationaleData] = useState<GenerateRationaleResponse | null>(null);
  const [isGeneratingRationale, setIsGeneratingRationale] = useState(false);
  const [editFieldsOpen, setEditFieldsOpen] = useState(false);
  const [scorecardDrawerOpen, setScorecardDrawerOpen] = useState(false);
  const editFieldsSnapshot = useRef<ContentType[]>([]);
  const [parsedZones, setParsedZones] = useState<Record<string, ParsedField | null>>({});
  const [parsingFields, setParsingFields] = useState<Set<string>>(new Set());
  const [parsedZonesA, setParsedZonesA] = useState<Record<string, ParsedField | null>>({});
  const [variantBFieldSources, setVariantBFieldSources] = useState<Record<string, "ai" | "ai-edited" | "manual">>({});
  const [selectedZone, setSelectedZone] = useState<ZoneSelection | null>(null);
  const [fingerprintData, setFingerprintData] = useState<{ fingerprint: AxisFingerprint; summary: string } | null>(null);
  const [fingerprintTarget, setFingerprintTarget] = useState<Record<string, number>>({ benefit: 0, audience: 0, "use-case": 0, ingredient: 0, format: 0 });
  const [isFingerprintLoading, setIsFingerprintLoading] = useState(false);
  const detectFingerprintMutation = useDetectFingerprint();
  const extractProductNameMutation = useExtractProductName();
  const skipPersistRef = useRef(false);
  // AUT-430 (WEB-008): this component's own persist-to-session-draft effect (below) and
  // useTestModalInit's restore-from-session-draft effect both fire on the same mount commit, in
  // hook-declaration order -- persist first. Without this guard, the persist effect's first run
  // (still-default state) overwrites the very session draft the restore branch is about to read,
  // discarding it before it is ever seen. skipPersistRef does not help here: useTestModalInit only
  // sets it inside its OWN effect, which runs after this one on that first commit. Found writing
  // the restore test for the strategy link (FR-15); it silently broke restore for every field, not
  // only the link.
  const hasPersistedOnceRef = useRef(false);
  // AUT-430 (WEB-009): the discard rule for the automatic hypothesis. Only the two applies that
  // write the hypothesis (keyword, strategy -- WEB-010, via useVariantBActions'
  // incrementHypothesisSeq) may write this ref; useCreateTestLogic reads it to drop a run its own
  // apply superseded.
  const applyHypothesisSeqRef = useRef(0);

  // AUT-396: re-validate Variant B when the user edits it, so the (non-blocking) verdict is visible
  // before Create rather than only at submit, where the success redirect hides it.
  const { revalidate: revalidateVariantB, isValidating: isValidatingCopy } = useRevalidateVariantB({
    parsedProductId, selectedBrandId, selectedFields, variantBTitle, variantBBullets, setCopyValidation,
  });

  const { data: scorecardList = [], isLoading: isLoadingScorecardList } = useListScorecards(heroAsin.asin);
  const latestScorecardId = scorecardList.length > 0 ? scorecardList[0].id : undefined;
  const { data: latestScorecard, isLoading: isLoadingScorecard } = useGetScorecard(latestScorecardId);
  const scorecardQuestions = useMemo<CosmoQuestion[]>(() => {
    if (!latestScorecard || latestScorecard.status !== "completed") return [];
    return (latestScorecard as { questions?: CosmoQuestion[] }).questions ?? [];
  }, [latestScorecard]);
  const derivedTestType: ABTestType = (() => {
    const hasIntent = intentTo !== "" && intentTo !== intentFrom;
    const hasKeyword = !!targetPhrase.trim();
    if (hasIntent && hasKeyword) return "intent-keyword";
    if (hasIntent) return "intent";
    if (hasKeyword) return "keyword";
    if (lastSource === "ai-format" || lastSource === "positioning-editor") return "format";
    return "custom";
  })();

  useEffect(() => {
    if (skipPersistRef.current || !open) return;
    if (!hasPersistedOnceRef.current) {
      // Skip the very first commit's write -- see hasPersistedOnceRef above. Any real change
      // (including the restore itself, once useTestModalInit sets state) re-runs this effect with
      // hasPersistedOnceRef already true, so nothing meaningful is lost.
      hasPersistedOnceRef.current = true;
      return;
    }
    // strategyLinkId is written as-is: JSON.stringify drops an undefined value, so "as stored"
    // (only ever true for a resumed server draft, which never persists to the session draft) is
    // never written -- an absent key on read falls back to initialStrategyLinkId.
    writeSessionDraft({ step, selectedFields, targetPhrase, intentFrom, intentTo, hypothesis, variantBTitle, variantBBullets, variantBMode, source: lastSource, sourceDetail: lastSourceDetail, appliedScorecardGaps, gapSnapshots, heroAsinId: String(heroAsin.id), strategyLinkId });
  }, [open, step, selectedFields, targetPhrase, intentFrom, intentTo, hypothesis, variantBTitle, variantBBullets, variantBMode, lastSource, lastSourceDetail, appliedScorecardGaps, gapSnapshots, heroAsin.id, strategyLinkId]);
  useEffect(() => {
    // Re-arm the mount guard above for the non-fullPage (Dialog) mode, where the same
    // CreateTestModal instance can close and reopen for a different test without unmounting.
    if (!open) hasPersistedOnceRef.current = false;
  }, [open]);

  const { undoStack, redoStack, pushHistory, handleUndo, handleRedo, resetStacks } = useUndoRedo({
    variantBTitle, variantBBullets, appliedScorecardGaps, lastSource, lastSourceDetail, rationaleData, gapSnapshots,
    strategyLinkId, targetPhrase,
    setVariantBTitle, setVariantBBullets, setAppliedScorecardGaps: v => setAppliedScorecardGaps(v),
    setLastSource, setLastSourceDetail, setRationaleData, setGapSnapshots: v => setGapSnapshots(v),
    setStrategyLinkId, setTargetPhrase,
  });
  const { handleApplyKeywordSource, handleApplyStrategy, handleApplyScorecardGapQuestion, handleApplySignal, keywordAdjustment, setKeywordAdjustment, unfulfilledLevers } = useVariantBActions({
    parsedProductId, selectedBrandId, selectedFields, hypothesis, targetPhrase, structureTreatments, variantATitle, variantABullets, variantBTitle, variantBBullets, appliedScorecardGaps,
    lastSource, lastSourceDetail, pushHistory, setIsGenerating, setLastSource, setLastSourceDetail, setHypothesis, setTargetPhrase, setStrategyLinkId, setAiStatus, setAppliedScorecardGaps, setGapSnapshots,
    setVariantBTitle, setVariantBBullets, setVariantBFieldSources, setParsedZones, setParsingFields, setIsGeneratingRationale, setRationaleData, setRationaleOpen,
    applyHypothesisSeqRef,
  });
  useTestModalInit({
    open, heroAsin, parsedProductId, selectedBrandId, initialTestType, initialTargetPhrase, initialStrategyLinkId,
    prefillFromDraft, prefillFromScorecard, prefillFromSignal, prefillFromKeyword, detectFingerprintMutation, extractProductNameMutation,
    setVisibleKeywordCount, setHypothesisValidation: v => setHypothesisValidation(v), setCopyValidation: v => setCopyValidation(v), setIsSaving, setStructureTreatments,
    setUndoStack: () => resetStacks(), setRedoStack: () => {}, setShowSuccess, setSuccessPayload: () => setSuccessPayload(null),
    setParsedZonesA: () => setParsedZonesA({}), setVariantBFieldSources: () => setVariantBFieldSources({}),
    setFingerprintData, setFingerprintTarget, setIsFingerprintLoading, setVariantATitle, setVariantABullets, setStep, setSelectedFields, setTargetPhrase, setStrategyLinkId, openTargetPhraseRef,
    setIntentFrom, setIntentTo, setHypothesis, setVariantBTitle, setVariantBBullets, setVariantBMode, setLastSource, setLastSourceDetail, setAppliedScorecardGaps, setGapSnapshots, skipPersistRef,
  });
  // AUT-430 (WEB-011), FR-2: re-decided on every open (the Dialog, non-fullPage mode reuses the
  // same instance across tests), not only at first mount -- mirrors useTestModalInit's own
  // `if (!open) return` gate. Not part of that hook: it is not persisted and does not read the
  // session draft.
  useEffect(() => {
    if (!open) return;
    setBuildTab(prefillFromScorecard ? "strategies" : "keywords");
  }, [open, prefillFromScorecard]);

  const scorecardAutoTriggerFiredRef = useRef(false);
  const handleApplyScorecardGapQuestionRef = useRef(handleApplyScorecardGapQuestion);
  handleApplyScorecardGapQuestionRef.current = handleApplyScorecardGapQuestion;
  const initialTargetPhraseRef = useRef(initialTargetPhrase);
  initialTargetPhraseRef.current = initialTargetPhrase;
  useEffect(() => {
    if (!prefillFromScorecard || step !== 2 || scorecardAutoTriggerFiredRef.current) return;

    const relationKeys = prefillFromScorecard.relationKeys ?? [];

    // An SEO Strategy card consolidates several COSMO questions, so every one of them
    // is applied. The real gaps come from this page's own scorecard rather than the
    // navigation state, which only carries the consolidated copy -- so wait for it.
    if (relationKeys.length > 0) {
      // Wait for this page's scorecard before deciding -- firing early would apply
      // nothing and burn the once-only flag.
      if (isLoadingScorecardList || isLoadingScorecard) return;

      const gaps = scorecardQuestions.filter(q => relationKeys.includes(q.relationKey));

      if (gaps.length > 0) {
        const targetKeyword = prefillFromScorecard.targetKeyword;

        scorecardAutoTriggerFiredRef.current = true;
        void (async () => {
          // Sequential: each apply rewrites Variant B on top of the previous one, the
          // same as clicking the gaps one after another.
          for (const gap of gaps) {
            await handleApplyScorecardGapQuestionRef.current(
              targetKeyword ? { ...gap, evidenceSnippet: targetKeyword } : gap,
            );
          }
        })();

        return;
      }
      // No match (no completed scorecard, or the questions have moved on): fall through
      // to the single consolidated gap, so this is never worse than before.
    }

    scorecardAutoTriggerFiredRef.current = true;
    void handleApplyScorecardGapQuestionRef.current({
      id: 0, relationKey: initialTargetPhraseRef.current ?? "", question: prefillFromScorecard.question,
      category: (prefillFromScorecard.category ?? "core_usage") as CosmoQuestion["category"],
      score: prefillFromScorecard.score ?? 0, status: (prefillFromScorecard.status ?? "weak") as CosmoQuestion["status"],
      finding: prefillFromScorecard.finding ?? "", recommendation: prefillFromScorecard.recommendation, evidenceSnippet: prefillFromScorecard.evidence,
    });
  }, [prefillFromScorecard, step, isLoadingScorecardList, isLoadingScorecard, scorecardQuestions]);

  // AUT-430 (WEB-012), FR-3: a keyword launch (the Keyword Bank's `Run A/B Test`, a landscape's
  // `Generate A/B Test`) applies the keyword once, on open, landing on `Keywords` -- once-only
  // guard in the same pattern as the scorecard auto-trigger above.
  // `components/ab-test.md § Target state — AUT-430 spec C`, *Launches*.
  const keywordAutoTriggerFiredRef = useRef(false);
  const handleApplyKeywordSourceRef = useRef(handleApplyKeywordSource);
  handleApplyKeywordSourceRef.current = handleApplyKeywordSource;
  useEffect(() => {
    if (
      !prefillFromKeyword || step !== 2 || keywordAutoTriggerFiredRef.current ||
      selectedBrandId == null || selectedFields.length === 0 || isLoadingKeywords ||
      (productKeywords === undefined && !isErrorKeywords)
    ) return;

    keywordAutoTriggerFiredRef.current = true;
    // Match the launch's phrase against the bank case-insensitively, so the apply carries the
    // row's real rank / search volume / KPS. With no match, or a bank query that errored, fall
    // back to the launch's own rank / searchVolume.
    const matchedRow = !isErrorKeywords
      ? productKeywords?.find(k => k.search.toLowerCase() === prefillFromKeyword.keyword.toLowerCase())
      : undefined;
    void handleApplyKeywordSourceRef.current(
      matchedRow ?? { search: prefillFromKeyword.keyword, rank: prefillFromKeyword.rank ?? null, search_volume: prefillFromKeyword.searchVolume ?? null },
      prefillFromKeyword.source,
    );
  }, [prefillFromKeyword, step, selectedBrandId, selectedFields.length, isLoadingKeywords, productKeywords, isErrorKeywords]);

  // AUT-430 (WEB-012), FR-3/H1: a Brand Signals launch applies the signal once, on open, landing
  // on `Keywords`, through the existing `handleApplySignal` / `useApplyBrandSignal` -- neither is
  // reworked by this task. Same once-only pattern as the two triggers above.
  const brandSignalAutoTriggerFiredRef = useRef(false);
  const handleApplySignalRef = useRef(handleApplySignal);
  handleApplySignalRef.current = handleApplySignal;
  useEffect(() => {
    if (!prefillFromSignal || step !== 2 || brandSignalAutoTriggerFiredRef.current || selectedBrandId == null || selectedFields.length === 0) return;
    brandSignalAutoTriggerFiredRef.current = true;
    void handleApplySignalRef.current(prefillFromSignal.statement);
  }, [prefillFromSignal, step, selectedBrandId, selectedFields.length]);

  const isVariantBLoading = isGenerating || parsingFields.size > 0 || isGeneratingRationale;
  const { handleSubmit, handleSaveAsPlanning } = useTestSubmit({
    parsedProductId, selectedBrandId, selectedFields, variantATitle, variantABullets, variantBTitle, variantBBullets, derivedTestType, targetPhrase, intentFrom, hypothesis,
    lastSource, lastSourceDetail, appliedScorecardGaps, strategyLinkId, onCreateTest, setIsSaving, setCopyValidation, setSuccessPayload: p => setSuccessPayload(p), setShowSuccess,
  });
  const { parsingFieldsA } = useCreateTestLogic({
    step, parsedProductId, selectedBrandId, selectedFields, variantATitle, variantABullets, variantBTitle, variantBBullets, derivedTestType, targetPhrase, fingerprintData, fingerprintTarget,
    parsedZonesA, setHypothesis, setHypothesisValidation, setParsedZones, setParsedZonesA: fn => setParsedZonesA(fn),
    applyHypothesisSeqRef,
  });

  const handleEditingFieldChange = useCallback(() => { setRationaleData(null); setRationaleOpen(false); setKeywordAdjustment(null); }, [setKeywordAdjustment]);
  const handleCopyVariantB = () => {
    const sections: string[] = [];
    if (selectedFields.includes("title") && variantBTitle) sections.push(`Title:\n${variantBTitle}`);
    const bLines = selectedFields.filter(f => f.startsWith("bullet-")).map((f, i) => variantBBullets[f] ? `${i + 1}. ${variantBBullets[f]}` : null).filter(Boolean);
    if (bLines.length) sections.push(`Bullets:\n${bLines.join("\n")}`);
    if (!sections.length) { toast({ title: "Nothing to copy", description: "Generate or enter Variant B content first.", variant: "destructive" }); return; }
    void navigator.clipboard.writeText(sections.join("\n\n"));
    toast({ title: "Copied", description: "Variant B content copied to clipboard." });
  };
  const handleResetVariantB = () => {
    pushHistory(); setVariantBTitle(variantATitle); setVariantBBullets({ ...variantABullets }); setVariantBFieldSources({}); setAppliedScorecardGaps([]); setGapSnapshots({}); setLastSource(null); setLastSourceDetail(""); setCopyValidation(null); setRationaleData(null); setRationaleOpen(false); setKeywordAdjustment(null); setAiStatus(null);
    // AUT-430 (WEB-008), FR-15/H2: clears a link `Use this strategy` made, never a launch's link;
    // returns the target phrase to its value at open. Neither touches the hypothesis.
    setStrategyLinkId(initialStrategyLinkId);
    setTargetPhrase(openTargetPhraseRef.current);
    toast({ title: "Reset", description: "Variant B reset to Variant A content." });
  };
  const handleEditFieldsSave = () => {
    if (JSON.stringify([...selectedFields].sort()) !== JSON.stringify([...editFieldsSnapshot.current].sort())) { setRationaleData(null); setRationaleOpen(false); setKeywordAdjustment(null); }
    setEditFieldsOpen(false);
  };
  const requestExit = () => {
    if (step === 2 && (variantBTitle.trim() !== "" || Object.values(variantBBullets).some(v => v.trim() !== ""))) { setExitDialogOpen(true); } else { clearSessionDraft(); handleCancel(); }
  };
  const rationaleButton = (rationaleData || isGeneratingRationale) ? (
    <Button type="button" variant="ghost" size="sm" onClick={() => setRationaleOpen(prev => !prev)} className="h-7 px-2 text-xs">
      <AISparkles className="h-3 w-3" />{isGeneratingRationale ? "Generating rationale…" : rationaleOpen ? "Hide rationale" : "View rationale"}
    </Button>
  ) : undefined;

  const step1 = <FieldSelectionStep selectedFields={selectedFields} onFieldToggle={field => setSelectedFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field])} bulletPoints={heroAsin.bulletPoints} />;
  const step2 = (
    <div className="flex flex-row min-w-0 -mx-9 -my-6 h-full overflow-hidden">
      <VariantAPane selectedFields={selectedFields} heroAsin={heroAsin} parsedZonesA={parsedZonesA} showColorCoding={true} />
      <VariantBPane selectedFields={selectedFields} variantBTitle={variantBTitle} variantBBullets={variantBBullets} variantATitle={variantATitle} variantABullets={variantABullets} onTitleChange={setVariantBTitle} onBulletChange={(field, val) => setVariantBBullets(prev => ({ ...prev, [field]: val }))} onCopy={handleCopyVariantB} onUndo={handleUndo} onRedo={handleRedo} onReset={handleResetVariantB} canUndo={undoStack.length > 0} canRedo={redoStack.length > 0} isParsing={parsingFields.size > 0 || parsingFieldsA.size > 0} isGenerating={isGenerating} aiStatus={aiStatus} fieldSources={variantBFieldSources} onFieldSourceChange={(field, source) => setVariantBFieldSources(prev => ({ ...prev, [field]: source }))} rationaleButton={rationaleButton} rationaleSlot={<RationaleSlot isGeneratingRationale={isGeneratingRationale} rationaleData={rationaleData} rationaleOpen={rationaleOpen} keywordAdjustment={keywordAdjustment} unfulfilledLevers={unfulfilledLevers} />} onFieldBlurWithChange={(field, value) => { pushHistory(); void revalidateVariantB({ field, value }); }} onEditingFieldChange={handleEditingFieldChange}>
        {/* AUT-430 (WEB-011), FR-1: the build tabs replace Use AI / From a starting point / Manual
            Editor -- `components/ab-test.md § Target state — AUT-430 spec C`, *The build tabs*. */}
        <div className="space-y-3">
          <Tabs value={buildTab} onValueChange={(value) => setBuildTab(value as "keywords" | "strategies")}>
            <TabsList variant="underline" className="w-full">
              <TabsTrigger variant="underline" value="keywords">Keywords</TabsTrigger>
              <TabsTrigger variant="underline" value="strategies">Strategies</TabsTrigger>
            </TabsList>
            <TabsContent value="keywords" className="space-y-3 pt-2">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">Keyword Bank</h4>
                <KeywordBankPicker
                  productId={Number(parsedProductId)}
                  heroAsin={heroAsin.asin}
                  keywords={productKeywords ?? []}
                  isLoading={isLoadingKeywords}
                  isError={isErrorKeywords}
                  onRetry={() => void refetchKeywords()}
                  isApplying={isGenerating}
                  onPickKeyword={(row) => void handleApplyKeywordSource(row.source)}
                />
              </div>
            </TabsContent>
            <TabsContent value="strategies" className="space-y-3 pt-2">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">Strategies</h4>
                <StrategyBrowserStates
                  asin={heroAsin.asin}
                  productId={Number(parsedProductId)}
                  brandName={heroAsin.brand}
                  onUseStrategy={handleApplyStrategy}
                  isApplyingStrategy={isGenerating}
                  segment={strategySegment}
                  onSegmentChange={setStrategySegment}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
        {isValidatingCopy && (
          <p className="text-xs text-muted-foreground" data-testid="variant-b-revalidating">
            Re-checking Variant B…
          </p>
        )}
        {copyValidation && <ValidationPanel validation={copyValidation} />}
      </VariantBPane>
      <aside className={cn("hidden lg:flex shrink-0 flex-col border-l border-border bg-card overflow-hidden transition-[width] duration-300 ease-out h-full", selectedZone ? "w-[380px]" : "w-0")} aria-hidden={!selectedZone}>
        {selectedZone && <ZoneEditPanel key={`${selectedZone.fieldId}:${selectedZone.zoneId}`} selection={selectedZone} onClose={() => setSelectedZone(null)} />}
      </aside>
    </div>
  );

  const successClose = () => { setShowSuccess(false); setSuccessPayload(null); if (fullPage) { handleCancel(); } else { onOpenChange(false); } };
  const sharedDialogs = (
    <>
      <TestSuccessDialog open={showSuccess} payload={successPayload} onClose={successClose} />
      <ExitConfirmDialog open={exitDialogOpen} onOpenChange={setExitDialogOpen} onDiscard={() => { clearSessionDraft(); handleCancel(); }} onSaveAndExit={() => { void handleSaveAsPlanning(() => handleCancel()); }} />
    </>
  );

  if (fullPage) return (
    <>
      <div className="fixed inset-0 z-50 bg-card flex flex-col">
        <CreateTestFullPageHeader heroAsin={heroAsin} step={step} selectedFields={selectedFields} isEditing={!!prefillFromDraft} onBack={requestExit} onEditFields={() => { editFieldsSnapshot.current = [...selectedFields]; setEditFieldsOpen(true); }} />
        <div className={cn("flex-1 bg-card min-h-0", step === 1 ? "overflow-y-auto" : "overflow-hidden flex flex-col")}><div className={cn("px-9 py-6 h-full", step === 2 && "flex flex-col min-h-0")}>{step === 1 ? step1 : step2}</div></div>
        <CreateTestFullPageFooter step={step} selectedFields={selectedFields} isSaving={isSaving} isVariantBLoading={step === 2 ? isVariantBLoading : false} isEditing={!!prefillFromDraft} onCancel={requestExit} onContinue={() => setStep(2)} onSaveTest={() => { void handleSaveAsPlanning(() => handleCancel()); }} onCreateTest={() => void handleSubmit()} />
      </div>
      {sharedDialogs}
      <EditFieldsDialog open={editFieldsOpen} selectedFields={selectedFields} bulletPoints={heroAsin.bulletPoints} onSelectedFieldsChange={setSelectedFields} onCancel={() => { setSelectedFields(editFieldsSnapshot.current); setEditFieldsOpen(false); }} onSave={handleEditFieldsSave} />
      <Sheet open={scorecardDrawerOpen} onOpenChange={setScorecardDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader className="mb-6"><SheetTitle>Amazon AI Score</SheetTitle></SheetHeader>
          {latestScorecard && latestScorecard.status === "completed" && <SPIScorecard result={latestScorecard as unknown as import("@/types/scorecard").SPIResult} hideBackButton hideProductInfo readOnly />}
        </SheetContent>
      </Sheet>
    </>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[580px] max-h-[90vh] flex flex-col gap-0 overflow-hidden bg-card p-0">
          <div className="shrink-0 border-b border-border px-6 pt-6 pb-4 pr-14">
            <DialogHeader className="space-y-3"><DialogTitle>Create New A/B Test</DialogTitle><StepIndicator step={step} onClickStep1={() => setStep(1)} /></DialogHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{step === 1 ? step1 : step2}</div>
          <div className="shrink-0 border-t border-border px-6 py-4">
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline-on-white" onClick={() => { if (step === 1) { clearSessionDraft(); onOpenChange(false); } else { setStep(1); } }}>{step === 1 ? "Cancel" : "Back"}</Button>
              {step === 1 ? <Button onClick={() => setStep(2)} disabled={!selectedFields.length}>Continue</Button> : (
                <>
                  <Button variant="outline" onClick={() => { void handleSaveAsPlanning(() => onOpenChange(false)); }} disabled={isSaving || isVariantBLoading} title={isVariantBLoading ? "Variant B is being generated — please wait" : undefined}>Save Test</Button>
                  <Button onClick={() => void handleSubmit()} disabled={isSaving || isVariantBLoading} title={isVariantBLoading ? "Variant B is being generated — please wait" : undefined}>{isSaving ? "Saving…" : "Create Test"}</Button>
                </>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {sharedDialogs}
      <RationaleDrawer open={rationaleOpen} onOpenChange={setRationaleOpen} rationale={rationaleData} isLoading={isGeneratingRationale} keywordAdjustment={keywordAdjustment} unfulfilledLevers={unfulfilledLevers} />
    </>
  );
}
